import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Calendar, Clock, MessageCircle, BarChart3, Target, Mic, Brain, TrendingUp, FileText, CheckSquare } from 'lucide-react'
import { MultiTypeAssessment } from '../../components/assessments/MultiTypeAssessment'
import { Navigation } from '../../components/Navigation'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { useAuth } from '../../hooks/useAuth'
import toast from 'react-hot-toast'

export const AssessmentPortal: React.FC = () => {
  const { appUser } = useAuth()
  const [currentView, setCurrentView] = useState<'dashboard' | 'assessment' | 'history'>('dashboard')
  const [recentAssessments, setRecentAssessments] = useState<any[]>([])
  const [upcomingAssessments, setUpcomingAssessments] = useState<any[]>([])

  useEffect(() => {
    loadAssessmentData()
  }, [appUser])

  const loadAssessmentData = async () => {
    // Mock data - would come from API
    setRecentAssessments([
      {
        id: '1',
        date: '2024-01-15',
        type: 'voice',
        sentiment_score: 0.75,
        engagement_level: 0.8,
        duration: 58
      },
      {
        id: '2',
        date: '2024-01-08',
        type: 'text',
        sentiment_score: 0.65,
        engagement_level: 0.7,
        duration: 45
      }
    ])

    setUpcomingAssessments([
      {
        id: '1',
        scheduled_date: '2024-01-22',
        type: 'mcq',
        title: 'Weekly Wellness Check-in'
      }
    ])
  }

  const handleAssessmentComplete = async (results: any) => {
    try {
      console.log('Assessment completed:', results)
      
      // Update recent assessments
      setRecentAssessments(prev => [
        {
          id: results.conversation_id || `assessment-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          type: results.assessment_type || 'multi',
          sentiment_score: results.overall_metrics.sentiment_score,
          engagement_level: results.overall_metrics.engagement_level,
          duration: results.duration_seconds || 0
        },
        ...prev
      ])

      // Return to dashboard
      setCurrentView('dashboard')
      
      toast.success('Assessment saved successfully!')
    } catch (error) {
      console.error('Error saving assessment:', error)
      toast.error('Failed to save assessment')
    }
  }

  const startAssessment = () => {
    setCurrentView('assessment')
  }

  const getAssessmentIcon = (type: string) => {
    switch (type) {
      case 'voice': return <Mic className="w-6 h-6 text-sage-600" />
      case 'text': return <FileText className="w-6 h-6 text-blue-600" />
      case 'mcq': return <CheckSquare className="w-6 h-6 text-purple-600" />
      default: return <MessageCircle className="w-6 h-6 text-sage-600" />
    }
  }

  const getAssessmentTitle = (type: string) => {
    switch (type) {
      case 'voice': return 'Voice Conversation'
      case 'text': return 'Text Assessment'
      case 'mcq': return 'Quick Survey'
      default: return 'Wellness Assessment'
    }
  }

  if (currentView === 'assessment') {
    return (
      <div className="min-h-screen bg-calming">
        <Navigation variant="dashboard" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => setCurrentView('dashboard')}
              className="text-slate-600 hover:text-slate-900 mb-4"
            >
              ← Back to Dashboard
            </Button>
          </div>

          <MultiTypeAssessment
            employeeId={appUser?.id || ''}
            onComplete={handleAssessmentComplete}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-calming">
      <Navigation variant="dashboard" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Your Wellness Journey
          </h1>
          <p className="text-slate-600">
            Track your mental health through AI-powered assessments and insights
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-sage-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-sage-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Wellness Score</p>
                <p className="text-2xl font-bold text-slate-900">
                  {recentAssessments.length > 0 
                    ? Math.round(recentAssessments[0].sentiment_score * 100)
                    : '--'
                  }%
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Brain className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Engagement</p>
                <p className="text-2xl font-bold text-slate-900">
                  {recentAssessments.length > 0 
                    ? Math.round(recentAssessments[0].engagement_level * 100)
                    : '--'
                  }%
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <MessageCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Assessments</p>
                <p className="text-2xl font-bold text-slate-900">{recentAssessments.length}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Avg Duration</p>
                <p className="text-2xl font-bold text-slate-900">
                  {recentAssessments.length > 0 
                    ? Math.round(recentAssessments.reduce((sum, a) => sum + a.duration, 0) / recentAssessments.length)
                    : '--'
                  }s
                </p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Assessment Options */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Start a Wellness Assessment</h2>
            
            <Card className="p-8 hover:shadow-lg transition-shadow cursor-pointer mb-8" onClick={startAssessment}>
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-sage-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Brain className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Complete Wellness Assessment</h3>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Choose from voice conversation, text responses, or quick survey to share how you're feeling and receive personalized insights.
                </p>
                <div className="flex items-center justify-center space-x-6 text-sm text-slate-500 mb-6">
                  <div className="flex items-center">
                    <Mic className="w-4 h-4 mr-1" />
                    <span>Voice</span>
                  </div>
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 mr-1" />
                    <span>Text</span>
                  </div>
                  <div className="flex items-center">
                    <CheckSquare className="w-4 h-4 mr-1" />
                    <span>Survey</span>
                  </div>
                </div>
                <Button className="bg-sage-600 hover:bg-sage-700 text-white">
                  Start Assessment
                </Button>
              </div>
            </Card>

            {/* Recent Assessments */}
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Recent Assessments</h2>
            
            {recentAssessments.length > 0 ? (
              <div className="space-y-4">
                {recentAssessments.map((assessment) => (
                  <Card key={assessment.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-sage-100 rounded-lg flex items-center justify-center mr-4">
                          {getAssessmentIcon(assessment.type)}
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-900">{getAssessmentTitle(assessment.type)}</h3>
                          <p className="text-sm text-slate-500">{assessment.date} • {assessment.duration}s</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-6">
                        <div className="text-center">
                          <div className="text-lg font-semibold text-slate-900">
                            {Math.round(assessment.sentiment_score * 100)}%
                          </div>
                          <div className="text-xs text-slate-500">Sentiment</div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-lg font-semibold text-slate-900">
                            {Math.round(assessment.engagement_level * 100)}%
                          </div>
                          <div className="text-xs text-slate-500">Engagement</div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">No assessments yet</h3>
                <p className="text-slate-600 mb-6">
                  Start your first wellness assessment to begin tracking your mental health journey
                </p>
                <Button
                  onClick={startAssessment}
                  className="bg-sage-600 hover:bg-sage-700 text-white"
                >
                  Start Your First Assessment
                </Button>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div>
            {/* Upcoming */}
            <Card className="p-6 mb-6">
              <h3 className="font-semibold text-slate-900 mb-4">Upcoming</h3>
              
              {upcomingAssessments.length > 0 ? (
                <div className="space-y-3">
                  {upcomingAssessments.map((assessment) => (
                    <div key={assessment.id} className="flex items-center">
                      <div className="w-8 h-8 bg-sage-100 rounded-lg flex items-center justify-center mr-3">
                        <Calendar className="w-4 h-4 text-sage-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{assessment.title}</p>
                        <p className="text-xs text-slate-500">{assessment.scheduled_date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No upcoming assessments</p>
              )}
            </Card>

            {/* Wellness Tips */}
            <Card className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Today's Wellness Tip</h3>
              <div className="bg-sage-50 rounded-lg p-4">
                <p className="text-sm text-sage-800 mb-2">
                  💬 Practice mindful conversation
                </p>
                <p className="text-xs text-sage-600">
                  When talking with our AI, speak naturally and honestly. The more authentic you are, 
                  the better insights you'll receive about your wellbeing.
                </p>
              </div>
            </Card>

            {/* Subscription Info */}
            <Card className="p-6 mt-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
              <h3 className="font-semibold text-slate-900 mb-4">Premium Features</h3>
              <div className="space-y-3 mb-4">
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <Brain className="w-3 h-3 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-700">Advanced AI Analysis</p>
                </div>
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <TrendingUp className="w-3 h-3 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-700">Personalized Insights</p>
                </div>
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <Target className="w-3 h-3 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-700">Goal Tracking</p>
                </div>
              </div>
              <Button
                onClick={() => window.open('https://bolt.new/setup/stripe', '_blank')}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
              >
                Upgrade to Premium
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}